type StatusEvent = { status: string; timestamp: number };

const downStatuses: ReadonlySet<string> = new Set([
  "browserClose",
  "qrReadFail",
  "autocloseCalled",
  "disconnectedMobile",
  "phoneNotConnected",
  "notLogged",
  "podIsDown",
  "streamMode:QR",
  "streamMode:TOS_BLOCK",
  "streamMode:SMB_TOS_BLOCK",
  "qrLoopStopped",
  "autoConnectDisabled",
  "noProxyAssigned",
]);

const isDownStatus = (status: string) =>
  downStatuses.has(status) || status.startsWith("qrAttempt:");

export const connectedStatus = "streamMode:MAIN";

const dayMs = 24 * 60 * 60 * 1000;

const closeOpenInterval = (
  downSince: number,
  lastEventTime: number | null | undefined,
  now: number,
) =>
  lastEventTime && lastEventTime > downSince
    ? Math.min(lastEventTime, now) - downSince
    : now - downSince;

export const downtimeMsLast24h = (
  statusHistory: StatusEvent[] | undefined,
  now: number,
  lastEventTime: number | null | undefined,
) => {
  if (!statusHistory) return 0;
  const windowStart = now - dayMs;
  const sorted = [...statusHistory].sort((a, b) => a.timestamp - b.timestamp);
  const previous = sorted.filter((e) => e.timestamp < windowStart).pop();
  const initialDownSince = previous && isDownStatus(previous.status)
    ? windowStart
    : null;
  const { downtime, downSince } = sorted
    .filter((e) => e.timestamp >= windowStart)
    .reduce<{ downtime: number; downSince: number | null }>(
      (acc, event) => {
        if (acc.downSince === null && isDownStatus(event.status)) {
          return { downtime: acc.downtime, downSince: event.timestamp };
        }
        if (acc.downSince !== null && event.status === connectedStatus) {
          return {
            downtime: acc.downtime + event.timestamp - acc.downSince,
            downSince: null,
          };
        }
        return acc;
      },
      { downtime: 0, downSince: initialDownSince },
    );
  return downSince === null
    ? downtime
    : downtime + closeOpenInterval(downSince, lastEventTime, now);
};

export const formatDowntime = (ms: number) => {
  const totalMinutes = Math.round(ms / 60000);
  const hours = Math.floor(totalMinutes / 60);
  const minutes = totalMinutes % 60;
  if (hours === 0) return `${minutes}m`;
  return minutes === 0 ? `${hours}h` : `${hours}h ${minutes}m`;
};

export const classifyStatus = (
  status: string | null | undefined,
): "green" | "yellow" | "red" | "gray" => {
  if (!status) return "gray";
  if (
    status === "streamMode:MAIN" ||
    status === "inChat" ||
    status === "connected" ||
    status === "isLogged" ||
    status === "qrReadSuccess"
  ) {
    return "green";
  }
  if (
    status === "startingDocker" ||
    status === "dockerStarted" ||
    status === "provisioningServer" ||
    status === "serverProvisioned" ||
    status === "waitingForSsh" ||
    status === "clientCreated" ||
    status === "streamMode:QR" ||
    status === "awaitingCode" ||
    status === "streamMode:SYNCING" ||
    status === "streamMode:OFFLINE" ||
    status.startsWith("qrAttempt:")
  ) {
    return "yellow";
  }
  if (
    downStatuses.has(status) ||
    status === "pod is down" ||
    status === "streamMode:CONFLICT" ||
    status === "streamMode:PROXYBLOCK" ||
    status === "streamMode:DEPRECATED_VERSION" ||
    status === "pairingCodeRateLimited" ||
    status === "loginFailed" ||
    status === "dailyMessageLimitReached"
  ) {
    return "red";
  }
  return "red"; // fallback to red to be safe
};

export type BinInfo = {
  start: number;
  end: number;
  category: "green" | "yellow" | "red" | "gray";
  statuses: string[];
  events: StatusEvent[];
};

export const downtimeBinsLast24h = (
  statusHistory: StatusEvent[] | undefined,
  now: number,
): BinInfo[] => {
  const hourMs = 60 * 60 * 1000;
  const history = statusHistory || [];
  const sorted = [...history].sort((a, b) => a.timestamp - b.timestamp);

  const bins: BinInfo[] = [];

  for (let i = 0; i < 24; i++) {
    const binStart = now - (24 - i) * hourMs;
    const binEnd = now - (23 - i) * hourMs;

    const lastBefore = sorted.filter((e) => e.timestamp <= binStart).pop();
    const startStatus = lastBefore ? lastBefore.status : null;

    const eventsInBin = sorted.filter(
      (e) => e.timestamp > binStart && e.timestamp <= binEnd,
    );

    const statusesInBin = new Set<string>();
    if (startStatus) statusesInBin.add(startStatus);
    eventsInBin.forEach((e) => statusesInBin.add(e.status));

    let category: "green" | "yellow" | "red" | "gray" = "gray";
    if (statusesInBin.size > 0) {
      const classifications = Array.from(statusesInBin).map(classifyStatus);
      if (classifications.includes("red")) {
        category = "red";
      } else if (classifications.includes("yellow")) {
        category = "yellow";
      } else if (classifications.includes("green")) {
        category = "green";
      }
    }

    bins.push({
      start: binStart,
      end: binEnd,
      category,
      statuses: Array.from(statusesInBin),
      events: eventsInBin,
    });
  }

  return bins;
};

export const uptimePercentageLast24h = (
  statusHistory: StatusEvent[] | undefined,
  now: number,
  lastEventTime: number | null | undefined,
): number => {
  if (!statusHistory || statusHistory.length === 0) return 100;
  const downtimeMs = downtimeMsLast24h(statusHistory, now, lastEventTime);
  const uptimeMs = Math.max(0, dayMs - downtimeMs);
  return (uptimeMs / dayMs) * 100;
};
